import { CloseMode, ModalWindow } from '@components/Modal/ModalWindow';
import { ModalWindowActions } from '@components/Modal/ModalWindowActions';
import { ModalWindowContent } from '@components/Modal/ModalWindowContent';
import { ModalWindowTitle } from '@components/Modal/ModalWindowTitle';
import { SecondaryButton } from '@components/Buttons/SecondaryButton';
import { IModalWindow } from '@hooks/useModalWindow';
import { DialogContentText, makeStyles, Typography } from '@material-ui/core';
import React from 'react';
import WarningIcon from '@material-ui/icons/Warning';
import useLoading from '@hooks/useLoading';
import { useContratoClienteService } from '@services/ContratoClienteService';
import useMessage from '@hooks/useMessage';
import { IContratoCliente } from '@model/ContratoCliente';
import { useCaptcha } from '@hooks/useCaptcha';
import { CaptchaField } from '@components/Fields/CaptchaField';
import ILinha from '@model/Linha';
import { useEffectOnce } from '@hooks/useEffectOnce';

export type LinhaCancelarModalProps = {
    contratoCliente: IContratoCliente
    modal: IModalWindow
    onContratoCancelado: (linha: ILinha) => void
    onModalClosed: VoidFunction
}

export function LinhaCancelarModal ({ contratoCliente, modal, onContratoCancelado, onModalClosed }: LinhaCancelarModalProps) {
    const cls = useStyles();

    const loading = useLoading();
    const message = useMessage();

    const captchaInputRef = React.useRef<HTMLInputElement>();
    const captcha = useCaptcha();

    const contratoClienteService = useContratoClienteService();

    const handleCancelar = React.useCallback(() => {
        if (!loading.isLoading) {
            loading.start();
            contratoClienteService.cancelarParaRegistrarOutro(contratoCliente.contratoClienteId)
            .then(response => {
                message.info("Contrato cancelado");
                modal.close();
                onContratoCancelado(response.data);
            })
            .catch(message.showException)
            .finally(loading.stop);
        }
    }, [contratoCliente.contratoClienteId, contratoClienteService, loading, message, modal, onContratoCancelado]);

    const handleModalClose = React.useCallback((closeMode: CloseMode) => {
        if (closeMode === "closeButton") {
            onModalClosed();
        }
    }, [onModalClosed]);

    useEffectOnce(() => {
        setTimeout(() => captchaInputRef.current?.focus(), 300);
    });

    return (
        <ModalWindow
            modal={modal}
            disabled={loading.isLoading}
            onModalClose={handleModalClose}
        >
            <ModalWindowTitle
                icon={<WarningIcon style={{color: 'orange'}} />}
                title="Linha já Vinculada"
            />

            <ModalWindowContent>
                <DialogContentText>A linha {contratoCliente.linhaNumero} está atualmente vincula ao contrato {contratoCliente.contratoClienteId} de {contratoCliente.clienteNome} ({contratoCliente.clienteCpf})</DialogContentText>
            </ModalWindowContent>

            <ModalWindowActions>
                <Typography variant="body2">Para confirmar informe o código <strong>{captcha.value}</strong></Typography>
                <CaptchaField 
                    inputRef={captchaInputRef}
                    captcha={captcha}
                    className={cls.captcha}
                    inputProps={{
                        className: cls.captchaInput
                    }}            
                />
                <SecondaryButton onClick={handleCancelar} disabled={!captcha.matched}>Confirmar Cancelamento</SecondaryButton>
            </ModalWindowActions>
        </ModalWindow>
    );
}

const useStyles = makeStyles(theme => ({
    captcha: {
        width: 60, 
    },

    captchaInput: {
        textTransform: 'uppercase',
        textAlign: 'center'
    }
}));